import { InboxOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Form, Input, Modal, Select, Upload, notification } from "antd";
import { _useAuth } from "hooks";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { httpClient } from "utils/Api";
import { ClientSelectedProgram } from "utils/Constant";
import { getCookieValue } from "utils/Storage";

const { Dragger } = Upload;

const MAX_FILE_SIZE = 500 * 1024; // 500 KB
const MAX_FILES = 3;

const SupportModal = ({ visible, onClose }) => {
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);

  const [form] = Form.useForm();
  const { authUser } = _useAuth();

  const enlisProgram = getCookieValue(ClientSelectedProgram);

  useEffect(() => {
    if (visible) {
      // prefill fields from authenticated user when modal opens
      form.setFieldsValue({
        email: authUser?.email || "",
        first_name: authUser?.first_name || authUser?.username || "",
        last_name: authUser?.last_name || "",
      });
    }
  }, [visible, authUser, form]);

  const beforeUpload = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      notification.error({
        message: "File too large",
        description: `${file.name} is larger than 500KB and was not added.`,
      });
      return Upload.LIST_IGNORE;
    }
    if (fileList.length >= MAX_FILES) {
      notification.error({
        message: "Too many files",
        description: `You can only upload up to ${MAX_FILES} images.`,
      });
      return Upload.LIST_IGNORE;
    }
    // Return false to prevent automatic upload - we handle files client-side and submit them with FormData
    return false;
  };

  // eslint-disable-next-line no-unused-vars
  const handleChange = ({ file, fileList: newList }) => {
    const filtered = newList.slice(0, MAX_FILES);
    setFileList(filtered);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();
      formData.append("issue", values.issue);
      formData.append("description", values.description || "");
      formData.append("program", enlisProgram);

      fileList.forEach((f, idx) => {
        // if file.originFileObj exists (from Upload), use it; otherwise skip
        const fileObj = f.originFileObj || f;
        if (fileObj)
          formData.append("images", fileObj, fileObj.name || `image_${idx}`);
      });

      setSubmitting(true);
      await mutation.mutateAsync(formData);

      notification.success({
        message: "Support request sent",
        description: "Thank you - our support team will get back to you.",
      });
      form.resetFields();
      setFileList([]);
      onClose();
    } catch (err) {
      if (err?.errorFields) {
        // validation error - let the form show errors
      } else {
        const errorData = err?.response?.data;
        let msg = "Failed to send support request";

        if (typeof errorData === "string") {
          msg = errorData;
        } else if (typeof errorData === "object" && errorData !== null) {
          // Flatten object errors: { issue: ["required"], desc: ["too short"] } -> "issue: required. desc: too short."
          msg = Object.entries(errorData)
            .map(
              ([key, val]) =>
                `${key}: ${Array.isArray(val) ? val.join(", ") : val}`,
            )
            .join(". ");
        } else if (err.message) {
          msg = err.message;
        }

        notification.error({ message: "Error", description: String(msg) });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const mutation = useMutation({
    mutationFn: (formData) =>
      httpClient.post("/support/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
  });

  return (
    <Modal
      title="Help & Support"
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Send"
      confirmLoading={submitting}
      width={700}
    >
      <div className="p-2">
        <Form form={form} layout="vertical">
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input placeholder="you@example.com" readOnly />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="First name"
              name="first_name"
              rules={[{ required: true }]}
            >
              <Input placeholder="First Name" readOnly />
            </Form.Item>
            <Form.Item
              label="Last name"
              name="last_name"
              rules={[{ required: true }]}
            >
              <Input placeholder="Last Name" readOnly />
            </Form.Item>
          </div>

          <Form.Item label="Issue" name="issue" rules={[{ required: true }]}>
            <Select placeholder="Select issue">
              <Select.Option value="bug">Bug Report</Select.Option>
              <Select.Option value="feature">Feature Request</Select.Option>
              <Select.Option value="access">Account / Access</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Please describe the issue" }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item label="Images (max 3, each ≤ 500KB)">
            <Dragger
              multiple
              accept="image/*"
              beforeUpload={beforeUpload}
              fileList={fileList}
              onChange={handleChange}
              listType="picture"
              showUploadList={{ showRemoveIcon: true }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Drag images here or click to upload
              </p>
              <p className="ant-upload-hint">
                PNG, JPG, GIF - up to {MAX_FILES} files, 500KB each.
              </p>
            </Dragger>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

SupportModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SupportModal;
